import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Unlock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CalendarManagementPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [propertyId]);

  const fetchData = async () => {
    try {
      const [propRes, calRes] = await Promise.all([
        axios.get(`${API}/properties/${propertyId}`),
        axios.get(`${API}/calendar/${propertyId}`)
      ]);
      setProperty(propRes.data);
      setCalendarEvents(calRes.data.events || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarEvents.some(event => event.date === dateStr && event.status === 'blocked');
  };

  const isDateBooked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarEvents.some(event => event.date === dateStr && event.status === 'booked');
  };

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates to block');
      return;
    }

    try {
      const datesToBlock = selectedDates.map(date => ({
        date: date.toISOString().split('T')[0],
        reason: 'Manually blocked'
      }));

      await axios.post(`${API}/calendar/${propertyId}/block`, datesToBlock);
      toast.success(`${selectedDates.length} date(s) blocked`);
      setSelectedDates([]);
      fetchData();
    } catch (error) {
      console.error('Error blocking dates:', error);
      toast.error('Error blocking dates');
    }
  };

  const handleUnblockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates to unblock');
      return;
    }

    try {
      const datesToUnblock = selectedDates.map(date => date.toISOString().split('T')[0]);
      await axios.delete(`${API}/calendar/${propertyId}/unblock`, { data: datesToUnblock });
      toast.success(`${selectedDates.length} date(s) unblocked`);
      setSelectedDates([]);
      fetchData();
    } catch (error) {
      console.error('Error unblocking dates:', error);
      toast.error('Error unblocking dates');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 md:px-8 lg:px-12">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Admin
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Manage Calendar: {property?.name}</CardTitle>
            <p className="text-muted-foreground">Select dates and block/unblock them manually</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="border rounded-lg p-4">
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={setSelectedDates}
                className="w-full"
                modifiers={{
                  booked: (date) => isDateBooked(date),
                  blocked: (date) => isDateBlocked(date)
                }}
                modifiersClassNames={{
                  booked: 'bg-red-200 text-red-900',
                  blocked: 'bg-yellow-200 text-yellow-900'
                }}
              />
            </div>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-200"></div>
                <span>Booked (Airbnb sync)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-200"></div>
                <span>Manually blocked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-200"></div>
                <span>Available</span>
              </div>
            </div>

            {selectedDates.length > 0 && (
              <div className="bg-muted p-4 rounded-lg">
                <p className="font-medium mb-2">{selectedDates.length} date(s) selected</p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleBlockDates}
                    className="flex items-center gap-2"
                  >
                    <Lock size={16} />
                    Block Selected Dates
                  </Button>
                  <Button
                    onClick={handleUnblockDates}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Unlock size={16} />
                    Unblock Selected Dates
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Dates marked as "Booked" come from your Airbnb calendar synchronization 
                and cannot be unblocked manually. To unblock them, update your Airbnb calendar.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CalendarManagementPage;
